import React, { useEffect, useMemo, useState } from "react";

import {
  FaUsers,
  FaUserCheck,
  FaChalkboardTeacher,
  FaUserShield,
} from "react-icons/fa";

import { getAllUsers } from "./../../../../../services/userServices";

function extractArray(data) {
  if (Array.isArray(data)) return data;

  if (data && typeof data === "object") {
    if (Array.isArray(data.rows)) return data.rows;
    if (Array.isArray(data.items)) return data.items;
    if (Array.isArray(data.users)) return data.users;
    if (Array.isArray(data.data)) return data.data;
  }

  return [];
}

function getRoleId(user) {
  return Number(
    user?.role_id ??
      user?.roleId ??
      user?.role?.id ??
      user?.Role?.id ??
      user?.user_role_id ??
      0
  );
}

/**
 * Date de création du compte User.
 * Dans ton modèle, le champ important est createdOn.
 */
function getUserCreatedDate(user) {
  return (
    user?.createdOn ??
    user?.created_on ??
    user?.createdON ??
    user?.created ??
    null
  );
}

function parseDateSafe(dateValue) {
  if (!dateValue) return null;

  // Timestamp numérique éventuel
  if (typeof dateValue === "number") {
    const date = new Date(dateValue);
    return Number.isNaN(date.getTime()) ? null : date;
  }

  const value = String(dateValue).trim();

  // Cas "YYYY-MM-DD" : date locale, évite les décalages UTC.
  if (/^\d{4}-\d{2}-\d{2}$/.test(value)) {
    const [year, month, day] = value.split("-").map(Number);
    return new Date(year, month - 1, day);
  }

  // Cas MySQL "YYYY-MM-DD HH:mm:ss"
  const normalizedValue = value.includes(" ") ? value.replace(" ", "T") : value;

  const date = new Date(normalizedValue);

  if (Number.isNaN(date.getTime())) return null;

  return date;
}

function isToday(dateValue) {
  const date = parseDateSafe(dateValue);

  if (!date) return false;

  const today = new Date();

  return (
    date.getFullYear() === today.getFullYear() &&
    date.getMonth() === today.getMonth() &&
    date.getDate() === today.getDate()
  );
}

function formatNumber(value) {
  return Number(value || 0).toLocaleString("fr-FR");
}

function StatCard({
  icon,
  label,
  value,
  helpText,
  accent = "indigo",
  loading = false,
  todayCount = null,
  todayLabel = "aujourd’hui",
}) {
  const accents = {
    indigo:
      "from-indigo-600/15 to-sky-500/10 text-indigo-700 dark:text-indigo-300 ring-indigo-200/70 dark:ring-indigo-500/20",
    emerald:
      "from-emerald-600/15 to-lime-500/10 text-emerald-700 dark:text-emerald-300 ring-emerald-200/70 dark:ring-emerald-500/20",
    amber:
      "from-amber-500/15 to-orange-500/10 text-amber-700 dark:text-amber-300 ring-amber-200/70 dark:ring-amber-500/20",
    violet:
      "from-violet-600/15 to-fuchsia-500/10 text-violet-700 dark:text-violet-300 ring-violet-200/70 dark:ring-violet-500/20",
  };

  const badgeClasses = {
    indigo:
      "bg-indigo-600/10 text-indigo-700 ring-indigo-200 dark:bg-indigo-500/10 dark:text-indigo-300 dark:ring-indigo-500/20",
    emerald:
      "bg-emerald-600/10 text-emerald-700 ring-emerald-200 dark:bg-emerald-500/10 dark:text-emerald-300 dark:ring-emerald-500/20",
    amber:
      "bg-amber-600/10 text-amber-700 ring-amber-200 dark:bg-amber-500/10 dark:text-amber-300 dark:ring-amber-500/20",
    violet:
      "bg-violet-600/10 text-violet-700 ring-violet-200 dark:bg-violet-500/10 dark:text-violet-300 dark:ring-violet-500/20",
  };

  return (
    <div
      className={`rounded-3xl bg-gradient-to-br ${accents[accent]} p-4 shadow-sm ring-1`}
    >
      <div className="flex items-start gap-3">
        <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-white/80 text-lg dark:bg-slate-900/50">
          {icon}
        </div>

        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <p className="text-[11px] uppercase tracking-[0.18em] opacity-70">
              {label}
            </p>

            {todayCount !== null && (
              <span
                className={`rounded-full px-2 py-1 text-[11px] font-bold ring-1 ${
                  badgeClasses[accent] || badgeClasses.indigo
                }`}
              >
                +{loading ? "…" : formatNumber(todayCount)} {todayLabel}
              </span>
            )}
          </div>

          <p className="mt-1 text-2xl font-bold text-slate-900 dark:text-white">
            {loading ? "…" : formatNumber(value)}
          </p>

          <p className="mt-1 text-xs leading-5 text-slate-600 dark:text-slate-300">
            {helpText}
          </p>
        </div>
      </div>
    </div>
  );
}

function ErrorCard({ message, onRetry }) {
  return (
    <div className="rounded-[28px] border border-rose-200 bg-rose-50 p-5 text-rose-700 dark:border-rose-500/20 dark:bg-rose-500/10 dark:text-rose-300">
      <p className="font-bold">Erreur de chargement</p>

      <p className="mt-1 text-sm leading-6">{message}</p>

      {onRetry ? (
        <button
          type="button"
          onClick={onRetry}
          className="mt-3 rounded-2xl bg-rose-600 px-4 py-2 text-sm font-bold text-white transition hover:bg-rose-700"
        >
          Réessayer
        </button>
      ) : null}
    </div>
  );
}

export default function StatisticsCards() {
  const [loading, setLoading] = useState(true);
  const [users, setUsers] = useState([]);
  const [errorMsg, setErrorMsg] = useState("");

  const fetchStats = async () => {
    try {
      setLoading(true);
      setErrorMsg("");

      const data = await getAllUsers();
      const list = extractArray(data);

      setUsers(list);
    } catch (error) {
      console.error("Erreur chargement statistiques utilisateurs :", error);
      setUsers([]);
      setErrorMsg(
        "Impossible de récupérer les statistiques des utilisateurs pour le moment."
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStats();
  }, []);

  const stats = useMemo(() => {
    const totalUsers = users.length;

    const utilisateurs = users.filter((user) => getRoleId(user) === 2).length;
    const enseignants = users.filter((user) => getRoleId(user) === 3).length;
    const admins = users.filter((user) => getRoleId(user) === 1).length;

    const newAccountsToday = users.filter((user) =>
      isToday(getUserCreatedDate(user))
    ).length;

    return {
      totalUsers,
      utilisateurs,
      enseignants,
      admins,
      newAccountsToday,
    };
  }, [users]);

  if (errorMsg) {
    return <ErrorCard message={errorMsg} onRetry={fetchStats} />;
  }

  return (
    <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
      <StatCard
        icon={<FaUsers />}
        label="Comptes"
        value={stats.totalUsers}
        helpText="Nombre total de comptes enregistrés."
        accent="indigo"
        loading={loading}
        todayCount={stats.newAccountsToday}
        todayLabel="aujourd’hui"
      />

      <StatCard
        icon={<FaUserCheck />}
        label="Utilisateurs"
        value={stats.utilisateurs}
        helpText="Comptes avec le rôle Utilisateur."
        accent="emerald"
        loading={loading}
      />

      <StatCard
        icon={<FaChalkboardTeacher />}
        label="Enseignants"
        value={stats.enseignants}
        helpText="Comptes avec le rôle Enseignant."
        accent="amber"
        loading={loading}
      />

      <StatCard
        icon={<FaUserShield />}
        label="Admins"
        value={stats.admins}
        helpText="Comptes administrateurs du club."
        accent="violet"
        loading={loading}
      />
    </div>
  );
}